import { Box } from '@mui/material';
import { BODY_CONTENT, CONTENT, STORY_CONTENT_CARD } from '@opengov/stories-shared/constants';
import { INewStoryContent, IStory, IStoryContent, IStoryMeta } from '@opengov/stories-shared/types';
import { orderStoryContents } from '@opengov/stories-shared/utils';
import update from 'immutability-helper';
import { isEqual } from 'lodash';
import React from 'react';
import { DragDropContext, DropTarget } from 'react-dnd';
import HTML5Backend from 'react-dnd-html5-backend';

import { IStoryViewMessages, IUpdateStoryContentProps } from '../../../../types';
import { StoryContentCard } from '../StoryContentCard';

import { styles } from './StoryContents.styles';

export type IStoryContentsProps = {
  entityName: string;
  storyMeta: IStoryMeta;
  story: IStory;
  isTemplate: boolean;
  storyContents: IStoryContent[];
  messages: IStoryViewMessages;
  connectDropTarget?(arg): JSX.Element;
  updateStoryContent(storyContent: IStoryContent | INewStoryContent): Promise<void>;
  updateStoryContents(storyContents: IStoryContent[]): Promise<void>;
  updateStoryContentDB(props: IUpdateStoryContentProps): Promise<void>;
  updateAllStoryContentsDB(props: {
    isTemplate: boolean;
    story: IStory;
    storyContents: IStoryContent[];
  }): Promise<void>;
  updateMetaStoryContent(storyContent: IStoryContent): Promise<void>;
  openModal(modal: string): IAction<string, string>;
  onClearContent(storyContent: IStoryContent): void;
  onResetContent(storyContent: IStoryContent): void;
};

export const StoryContents = DragDropContext(HTML5Backend)(
  DropTarget(STORY_CONTENT_CARD, {}, (connect) => ({
    connectDropTarget: connect.dropTarget()
  }))(
    class StoryContentsBase extends React.Component<IStoryContentsProps> {
      shouldComponentUpdate(nextProps: IStoryContentsProps) {
        // Only re-render if storyContents or storyContentInView props have changed
        return (
          !isEqual(this.props.storyContents, nextProps.storyContents) ||
          !isEqual(this.props.storyMeta.storyContentInView, nextProps.storyMeta.storyContentInView)
        );
      }

      moveStoryContent = (storyContentId: string, atIndex: number) => {
        const { storyContents, updateStoryContents, updateAllStoryContentsDB, story, isTemplate } =
          this.props;
        const { storyContent, index } = this.findStoryContent(storyContentId);
        const newStoryContents = update(storyContents, {
          $splice: [
            [index, 1],
            [atIndex, 0, storyContent]
          ]
        });
        const orderedStoryContents = orderStoryContents(newStoryContents);

        // Update the store and the DB.
        void updateStoryContents(orderedStoryContents);
        void updateAllStoryContentsDB({
          isTemplate: isTemplate,
          story: story,
          storyContents: orderedStoryContents
        });
      };

      findStoryContent = (storyContentId: string) => {
        const { storyContents } = this.props;
        const storyContent = storyContents.filter((c) => c._id === storyContentId)[0];

        return {
          storyContent,
          index: storyContents.indexOf(storyContent)
        };
      };

      render() {
        const {
          connectDropTarget,
          entityName,
          storyMeta: { storyContentInView },
          storyContents,
          messages,
          updateStoryContent,
          updateMetaStoryContent,
          openModal,
          story,
          updateStoryContentDB,
          isTemplate,
          onClearContent,
          onResetContent
        } = this.props;

        return connectDropTarget!(
          <div>
            <Box sx={styles.root}>
              {storyContents.map((storyContent) => (
                <StoryContentCard
                  key={`storyContent-${storyContent._id}`}
                  entityName={entityName}
                  isTemplate={isTemplate}
                  story={story}
                  storyContentInView={storyContentInView}
                  storyContent={storyContent}
                  firstId={storyContents[0]._id}
                  updateStoryContentDB={updateStoryContentDB}
                  lastId={storyContents[storyContents.length - 1]._id}
                  canDrag={storyContent.type === CONTENT || storyContent.type === BODY_CONTENT}
                  moveStoryContent={this.moveStoryContent}
                  findStoryContent={this.findStoryContent}
                  messages={messages}
                  updateStoryContent={updateStoryContent}
                  updateMetaStoryContent={updateMetaStoryContent}
                  openModal={openModal}
                  onClearContent={onClearContent}
                  onResetContent={onResetContent}
                />
              ))}
            </Box>
          </div>
        );
      }
    }
  )
);
